/** @jsxImportSource theme-ui */
import { Text } from "@bottlebooks/gatsby-design-system";
import { Link } from "@bottlebooks/gatsby-theme-base/src";
import { Trans } from "@lingui/macro";
import { Fragment } from "react";
import { FragmentType, graphql, useFragment } from "~/gql";
import useLink from "../../useLink";
import Skeleton from "../Skeleton";
import type { TextProps } from "./UserProfileCard";

const fragment = graphql(`
  fragment UserProfileCardCompany on SiteUserProfile {
    jobTitle
    companyName
    registration {
      companyId
    }
  }
`);

export default function UserProfileCardCompany({
  profile: data,
  linkToExhibitor,
  ...rest
}: TextProps & {
  profile: FragmentType<typeof fragment>;
  linkToExhibitor?: boolean;
}) {
  const profile = useFragment(fragment, data);
  if (!profile) return null;
  const { jobTitle, companyName } = profile;
  return (
    <Text variant="smallest" sx={{ color: "lighterText" }} {...rest}>
      {jobTitle && companyName ? (
        <Trans>
          {jobTitle} at{" "}
          <CompanyName data={data} linkToExhibitor={linkToExhibitor} />
        </Trans>
      ) : (
        <Fragment>
          {jobTitle}
          <CompanyName data={data} linkToExhibitor={linkToExhibitor} />
        </Fragment>
      )}
    </Text>
  );
}

function CompanyName({
  data,
  linkToExhibitor,
}: {
  data: FragmentType<typeof fragment>;
  linkToExhibitor?: boolean;
}) {
  const profile = useFragment(fragment, data);
  const link = useLink();
  if (!profile.companyName) return null;
  if (!linkToExhibitor) return <Fragment>{profile.companyName}</Fragment>;
  if (!profile.registration?.companyId)
    return <Fragment>{profile.companyName}</Fragment>;
  return (
    <Link
      variant="text"
      to={link.exhibitor({ exhibitorId: profile.registration.companyId })}
    >
      {profile.companyName}
    </Link>
  );
}

export type UserProfileCardCompanyData = {
  companyName: string | null | undefined;
  jobTitle: string | null | undefined;
  registration: {
    companyId: string | null;
  } | null;
};

export function UserProfileCardCompanySkeleton(rest: TextProps) {
  return (
    <Text variant="smallest" sx={{ paddingBottom: 1 }} {...rest}>
      <Skeleton width="6em" height="1em" />
    </Text>
  );
}
